import { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Loader2, CheckCircle2, AlertCircle, MailCheck } from "lucide-react";
import { useCustomerAuth, describeAxiosError } from "@/contexts/CustomerAuthContext";
import AuthShell from "@/components/customer/AuthShell";
import SEO from "@/components/site/SEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/customer/auth`;

export default function VerifyEmail() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const { verifyEmail } = useCustomerAuth();
  const nav = useNavigate();

  // States: idle (waiting for user click) | loading | success | error
  const [state, setState] = useState(token ? "idle" : "error");
  const [error, setError] = useState(token ? "" : "No verification token found in the link.");

  // Resend-verification helpers (used when the token has already been consumed)
  const [resendEmail, setResendEmail] = useState("");
  const [resending, setResending] = useState(false);
  const [resendOk, setResendOk] = useState(false);
  const [resendErr, setResendErr] = useState("");

  async function handleConfirm() {
    setState("loading");
    setError("");
    try {
      await verifyEmail(token);
      setState("success");
      setTimeout(() => nav("/app", { replace: true }), 1800);
    } catch (err) {
      setState("error");
      setError(describeAxiosError(err, "Verification failed."));
    }
  }

  async function handleResend(e) {
    e.preventDefault();
    setResending(true);
    setResendOk(false);
    setResendErr("");
    try {
      await axios.post(`${API}/resend-verification`, { email: resendEmail.trim().toLowerCase() });
      setResendOk(true);
    } catch (err) {
      setResendErr(describeAxiosError(err, "Could not resend verification email."));
    } finally {
      setResending(false);
    }
  }

  return (
    <AuthShell
      eyebrow="Email verification"
      title={state === "success" ? "You're verified" : "Confirm your email"}
      subtitle={
        state === "success"
          ? "Welcome to OneDial Africa."
          : "Click the button below to finish creating your account."
      }
    >
      <SEO title="Verify your email" path="/verify-email" />
      <div className="rounded-xl border border-brand-line bg-brand-bgSoft p-6 text-sm">
        {state === "idle" && (
          <div className="space-y-4" data-testid="verify-idle">
            <p className="text-brand-ink">
              For your security, we need one extra click to confirm it&rsquo;s really you.
            </p>
            <button
              type="button"
              onClick={handleConfirm}
              data-testid="verify-confirm-btn"
              className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-brand-green px-6 py-3 text-base font-semibold text-white shadow-sm transition hover:bg-brand-greenHover focus:outline-none focus:ring-2 focus:ring-brand-green focus:ring-offset-2"
            >
              <MailCheck className="h-5 w-5" /> Confirm my email
            </button>
            <p className="text-xs text-brand-muted">
              This link is single-use and expires in 24 hours.
            </p>
          </div>
        )}

        {state === "loading" && (
          <div className="flex items-center gap-3 text-brand-ink" data-testid="verify-loading">
            <Loader2 className="h-5 w-5 animate-spin text-brand-green" /> Verifying&hellip;
          </div>
        )}

        {state === "success" && (
          <div className="flex items-start gap-3 text-brand-ink" data-testid="verify-success">
            <CheckCircle2 className="h-5 w-5 text-brand-green mt-0.5" />
            <div>
              <p className="font-semibold">Email verified.</p>
              <p className="text-brand-muted mt-1">Taking you to your dashboard&hellip;</p>
            </div>
          </div>
        )}

        {state === "error" && (
          <div className="space-y-5" data-testid="verify-error">
            <div className="flex items-start gap-3 text-brand-ink">
              <AlertCircle className="h-5 w-5 text-red-500 mt-0.5" />
              <div>
                <p className="font-semibold">We couldn&rsquo;t verify your email.</p>
                <p className="text-brand-muted mt-1">{error}</p>
              </div>
            </div>

            <div className="rounded-lg border border-brand-line bg-white p-4">
              <p className="text-sm font-semibold text-brand-ink">
                Send me a fresh verification link
              </p>
              <p className="mt-1 text-xs text-brand-muted">
                Email scanners sometimes consume the original link before you click. Enter your
                email and we&rsquo;ll send a new one.
              </p>
              <form onSubmit={handleResend} className="mt-3 flex flex-col gap-2 sm:flex-row">
                <input
                  type="email"
                  required
                  autoComplete="email"
                  value={resendEmail}
                  onChange={(e) => setResendEmail(e.target.value)}
                  placeholder="you@example.com"
                  data-testid="verify-resend-email"
                  className="flex-1 rounded-lg border border-brand-line bg-white px-3 py-2 text-sm text-brand-ink placeholder-brand-muted focus:border-brand-green focus:outline-none focus:ring-1 focus:ring-brand-green"
                />
                <button
                  type="submit"
                  disabled={resending}
                  data-testid="verify-resend-btn"
                  className="rounded-full bg-brand-green px-5 py-2 text-sm font-semibold text-white transition hover:bg-brand-greenHover disabled:opacity-60"
                >
                  {resending ? "Sending…" : "Resend"}
                </button>
              </form>
              {resendOk && (
                <p className="mt-2 text-xs text-brand-green" data-testid="verify-resend-ok">
                  If that email is registered, a new verification link is on its way.
                </p>
              )}
              {resendErr && (
                <p className="mt-2 text-xs text-red-600" data-testid="verify-resend-err">
                  {resendErr}
                </p>
              )}
            </div>

            <Link
              to="/login"
              className="inline-block text-sm font-semibold text-brand-green hover:text-brand-greenHover"
            >
              Back to sign in &rarr;
            </Link>
          </div>
        )}
      </div>
    </AuthShell>
  );
}
